import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostListener,
  input,
  OnDestroy,
  output,
  viewChild,
} from '@angular/core';
import { Params, RouterLink } from '@angular/router';

export type PopupTone = 'default' | 'success' | 'error';
export type PopupButtonsAlign = 'start' | 'center' | 'end';
export type PopupActionWidth = 'fit' | 'full' | 'flex' | 'medium';

export interface PopupAction {
  label: string;
  loadingLabel?: string;
  link?: string | any[];
  queryParams?: Params;
  closeOnClick?: boolean;
  disabled?: boolean;
  tone?: PopupTone;
  width?: PopupActionWidth;
}

let popupId = 0;

@Component({
  selector: 'app-popup',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './popup.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Popup implements AfterViewInit, OnDestroy {
  private static readonly openPopups: Popup[] = [];

  readonly title = input.required<string>();
  readonly message = input<string>('');
  readonly tone = input<PopupTone>('default');
  readonly primaryButton = input<PopupAction | null>(null);
  readonly secondaryButton = input<PopupAction | null>(null);
  readonly closeOnBackdrop = input(true);
  readonly closeOnEscape = input(false);
  readonly showIconBackground = input(true);
  readonly buttonsAlign = input<PopupButtonsAlign>('center');
  readonly showCloseButton = input(false);
  readonly busy = input(false);
  readonly referenceStyle = input(false);

  readonly titleId = `popup-title-${++popupId}`;
  readonly messageId = `popup-message-${popupId}`;
  private readonly panel = viewChild.required<ElementRef<HTMLElement>>('panel');
  private previouslyFocusedElement: HTMLElement | null = null;
  private isDestroying = false;

  readonly closed = output<void>();
  readonly primaryAction = output<void>();
  readonly secondaryAction = output<void>();

  readonly hasButtons = computed(
    () => !!this.primaryButton() || !!this.secondaryButton(),
  );

  readonly titleClass = computed(() => {
    if (this.referenceStyle()) return 'text-[#1A1A1A]';

    switch (this.tone()) {
      case 'success':
        return 'text-[#0E7A4B]';
      case 'error':
        return 'text-[#A63A3A]';
      default:
        return 'text-[#114455]';
    }
  });
  readonly iconClass = computed(() => {
    if (this.referenceStyle()) {
      switch (this.tone()) {
        case 'success':
          return 'bg-gradient-to-b from-[#65C5A4] to-[#078355]';
        case 'error':
          return 'bg-gradient-to-b from-[#FF777B] to-[#DD1E2B]';
        default:
          return 'bg-gradient-to-b from-[#55C1E3] to-[#127F9F]';
      }
    }

    switch (this.tone()) {
      case 'success':
        return 'bg-[#0E7A4B]';
      case 'error':
        return 'bg-[#A63A3A]';
      default:
        return 'bg-[#184B63]';
    }
  });

  readonly panelClass = computed(() =>
    this.referenceStyle()
      ? 'max-w-[500px] sm:w-[500px] sm:min-w-[500px]'
      : 'max-w-[457px] sm:w-fit sm:min-w-[416px]',
  );

  getPrimaryButtonClass(action: PopupAction | null) {
    if (this.referenceStyle()) {
      switch (action?.tone ?? this.tone()) {
        case 'success':
          return 'bg-[#0DA64F] hover:bg-[#0B9145] text-white';
        case 'error':
          return 'bg-[#D14343] hover:bg-[#B93636] text-white';
        default:
          return 'bg-[#218EAF] hover:bg-[#1B7895] text-white';
      }
    }

    switch (action?.tone ?? this.tone()) {
      case 'success':
        return 'bg-[#0E9F6E] hover:bg-[#0B8A5F] text-white';
      case 'error':
        return 'bg-[#D14343] hover:bg-[#BA3434] text-white';
      default:
        return 'bg-[#2187AB] hover:bg-[#1B6E8C] text-white';
    }
  }

  readonly buttonsContainerClass = computed(() => {
    switch (this.buttonsAlign()) {
      case 'start':
        return 'justify-start';
      case 'end':
        return 'justify-end';
      default:
        return 'justify-center';
    }
  });

  ngAfterViewInit() {
    this.previouslyFocusedElement = document.activeElement as HTMLElement | null;
    Popup.openPopups.push(this);
    this.panel().nativeElement.focus();
  }

  ngOnDestroy() {
    this.isDestroying = true;
    const popupIndex = Popup.openPopups.indexOf(this);
    if (popupIndex !== -1) Popup.openPopups.splice(popupIndex, 1);
    this.previouslyFocusedElement?.focus();
  }

  onBackdropClick() {
    if (this.busy() || !this.closeOnBackdrop()) {
      return;
    }

    this.closed.emit();
  }

  onPrimaryClick(event?: Event) {
    const action = this.primaryButton();
    if (this.busy() || action?.disabled) {
      event?.preventDefault();
      return;
    }

    this.primaryAction.emit();

    if (action?.closeOnClick ?? true) {
      this.closed.emit();
    }
  }

  onSecondaryClick(event?: Event) {
    const action = this.secondaryButton();
    if (this.busy() || action?.disabled) {
      event?.preventDefault();
      return;
    }

    this.secondaryAction.emit();

    if (action?.closeOnClick ?? true) {
      this.closed.emit();
    }
  }

  onCloseClick() {
    if (!this.busy()) this.closed.emit();
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: Event) {
    if (!this.isTopmost()) return;
    if (!this.closeOnEscape()) return;

    if (this.busy()) {
      event.preventDefault();
      return;
    }

    this.closed.emit();
  }

  @HostListener('document:focusin', ['$event'])
  onDocumentFocus(event: FocusEvent) {
    if (
      !this.isDestroying &&
      this.isTopmost() &&
      !this.panel().nativeElement.contains(event.target as Node)
    ) {
      this.panel().nativeElement.focus();
    }
  }

  @HostListener('document:keydown.tab', ['$event'])
  onTab(event: Event) {
    if (!this.isTopmost()) return;

    const keyboardEvent = event as KeyboardEvent;
    const panel = this.panel().nativeElement;
    const focusableElements = Array.from(
      panel.querySelectorAll<HTMLElement>(
        'a[href]:not([aria-disabled="true"]), button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [contenteditable="true"], [tabindex]:not([tabindex="-1"])',
      ),
    );
    const first = focusableElements[0];
    const last = focusableElements.at(-1);
    const activeElement = document.activeElement as HTMLElement | null;

    if (!first || !last) {
      keyboardEvent.preventDefault();
      panel.focus();
      return;
    }

    if (
      !activeElement ||
      !panel.contains(activeElement) ||
      (activeElement !== panel && !focusableElements.includes(activeElement))
    ) {
      keyboardEvent.preventDefault();
      (keyboardEvent.shiftKey ? last : first).focus();
      return;
    }

    if (
      keyboardEvent.shiftKey &&
      (activeElement === first || activeElement === panel)
    ) {
      keyboardEvent.preventDefault();
      last.focus();
    } else if (!keyboardEvent.shiftKey && activeElement === last) {
      keyboardEvent.preventDefault();
      first.focus();
    }
  }

  private isTopmost() {
    return Popup.openPopups.at(-1) === this;
  }

  isLinkAction(
    action: PopupAction | null,
  ): action is PopupAction & { link: string | any[] } {
    return !!action?.link;
  }

  getAbsoluteLink(action: PopupAction | null): string | any[] | null {
    const link = action?.link;
    if (!link) {
      return null;
    }

    if (typeof link === 'string') {
      return link.startsWith('/') ? link : `/${link}`;
    }

    if (!link.length) {
      return ['/'];
    }

    const [firstSegment, ...rest] = link;
    if (typeof firstSegment === 'string') {
      return [
        firstSegment.startsWith('/') ? firstSegment : `/${firstSegment}`,
        ...rest,
      ];
    }

    return ['/', ...link];
  }

  getActionWidthClass(action: PopupAction | null): string {
    switch (action?.width ?? 'fit') {
      case 'full':
        return 'w-full';
      case 'flex':
        return 'flex-1 basis-0';
      case 'medium':
        return 'w-55';
      default:
        return 'w-fit';
    }
  }

  getPrimaryActionWidthClass(action: PopupAction | null): string {
    if (!this.secondaryButton() && !action?.width) {
      return 'w-55';
    }

    return this.getActionWidthClass(action);
  }
}
